import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import BrandMark from "@/components/BrandMark";
import OutfitCard from "@/components/OutfitCard";
import StyleGapSection from "@/components/StyleGapSection";
import { ChevronLeft } from "lucide-react";

export default function OutfitResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(location.state || null);
  const [loadingAnother, setLoadingAnother] = useState(false);
  const [loadingWore, setLoadingWore] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!data) {
      navigate("/home", { replace: true });
      return;
    }
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, [data, navigate]);

  if (!data) return null;

  const another = async () => {
    setLoadingAnother(true);
    try {
      const excl = [...excluded, data.suggestion.suggestion_id];
      const { data: res } = await api.post("/suggest", {
        occasion: data.suggestion.occasion,
        mood: data.suggestion.mood,
        exclude_suggestion_ids: excl,
      });
      setExcluded(excl);
      setData(res);
      setFeedback(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No more options — try a different occasion.");
    } finally {
      setLoadingAnother(false);
    }
  };

  const wore = async () => {
    setLoadingWore(true);
    try {
      await api.post("/wear/confirm", {
        suggestion_id: data.suggestion.suggestion_id,
        item_ids: data.suggestion.item_ids,
        occasion: data.suggestion.occasion,
        mood: data.suggestion.mood,
        confirmed: true,
      });
      toast.success("Logged. I'll remember this look.");
      navigate("/home");
    } finally {
      setLoadingWore(false);
    }
  };

  const thumb = async (rating) => {
    setFeedback(rating);
    await api.post("/feedback", {
      suggestion_id: data.suggestion.suggestion_id,
      rating,
    });
    toast.success(rating === "up" ? "Noted — more like this." : "Thanks — I'll adjust.");
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0.75rem))" }}>
      <div className="max-w-md mx-auto px-6 pt-12">
        <button onClick={() => navigate("/home")} className="mb-4 flex items-center gap-2 text-sm font-[Manrope] text-stone-600">
          <ChevronLeft size={16} /> back
        </button>
        <BrandMark className="mb-4" />
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-1">
          Today&rsquo;s look
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900 mb-6">
          For <em className="italic">{data.suggestion.occasion.toLowerCase()}</em>
        </h1>

        <OutfitCard
          suggestion={data.suggestion}
          items={data.items}
          weather={data.weather}
          onAnother={another}
          onWore={wore}
          onThumb={thumb}
          loadingAnother={loadingAnother}
          loadingWore={loadingWore}
          feedbackGiven={feedback}
        />

        <StyleGapSection
          key={data.suggestion.suggestion_id}
          suggestionId={data.suggestion.suggestion_id}
          gender={profile?.gender === "male" ? "men" : "women"}
          occasion={data.suggestion.occasion}
          vibe={data.suggestion.vibe}
        />
      </div>
      <BottomNav />
    </div>
  );
}
